const DOT_ACTIVE_CLASS = "tw-static-usps-dot--active";

const buildDots = (
  container: HTMLElement,
  slideCount: number,
  onSelect: (index: number) => void,
  onInteract: () => void,
): void => {
  container.replaceChildren();

  for (let index = 0; index < slideCount; index++) {
    const dot = document.createElement("button");
    dot.type = "button";
    dot.className = `tw-static-usps-dot${index === 0 ? ` ${DOT_ACTIVE_CLASS}` : ""}`;
    dot.setAttribute("aria-label", `Slide ${index + 1}`);
    dot.addEventListener("click", () => {
      onInteract();
      onSelect(index);
    });
    container.appendChild(dot);
  }
};

const updateDots = (container: HTMLElement, activeIndex: number): void => {
  const dots = Array.from(container.querySelectorAll<HTMLButtonElement>(".tw-static-usps-dot"));

  dots.forEach((dot, index) => {
    dot.classList.toggle(DOT_ACTIVE_CLASS, index === activeIndex);
  });
};

const syncIndexFromScroll = (track: HTMLElement, onChange: (index: number) => void): void => {
  const index = Math.round(track.scrollLeft / track.clientWidth);
  onChange(index);
};

export function initTwStaticUsps(root: ParentNode = document): void {
  const sliders = Array.from(root.querySelectorAll<HTMLElement>("[data-tw-static-usps-slider]:not([data-slider-init])"));

  for (const slider of sliders) {
    const track = slider.querySelector("[data-tw-static-usps-track]") as HTMLElement | null;
    const dotsContainer = slider.querySelector("[data-tw-static-usps-dots]") as HTMLElement | null;
    const slides = track ? Array.from(track.children).filter((child): child is HTMLElement => child instanceof HTMLElement) : [];

    if (!track || !dotsContainer || slides.length === 0) continue;

    slider.dataset.sliderInit = "true";

    if (slides.length <= 1) {
      dotsContainer.hidden = true;
      continue;
    }

    let currentIndex = 0;
    const autoplayMs = parseInt(slider.dataset.autoplay ?? "0", 10);
    let autoplayTimer: ReturnType<typeof setInterval> | null = null;

    const stopAutoplay = (): void => {
      if (!autoplayTimer) return;
      clearInterval(autoplayTimer);
      autoplayTimer = null;
    };

    const goTo = (index: number): void => {
      currentIndex = Math.max(0, Math.min(index, slides.length - 1));
      track.scrollTo({ left: track.clientWidth * currentIndex, behavior: "smooth" });
      updateDots(dotsContainer, currentIndex);
    };

    const startAutoplay = (): void => {
      if (autoplayMs <= 0) return;
      stopAutoplay();
      autoplayTimer = setInterval(() => {
        goTo((currentIndex + 1) % slides.length);
      }, autoplayMs);
    };

    buildDots(dotsContainer, slides.length, goTo, stopAutoplay);

    track.addEventListener("touchstart", stopAutoplay, { passive: true });

    track.addEventListener(
      "scrollend",
      () => {
        syncIndexFromScroll(track, (index) => {
          if (index === currentIndex) return;
          currentIndex = index;
          updateDots(dotsContainer, currentIndex);
        });
      },
      { passive: true },
    );

    let scrollTimer: ReturnType<typeof setTimeout>;
    track.addEventListener(
      "scroll",
      () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          syncIndexFromScroll(track, (index) => {
            if (index === currentIndex) return;
            currentIndex = index;
            updateDots(dotsContainer, currentIndex);
          });
        }, 100);
      },
      { passive: true },
    );

    startAutoplay();
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initTwStaticUsps());
} else {
  initTwStaticUsps();
}

document.addEventListener("shopify:section:load", (event: Event) => {
  const target = (event as CustomEvent).target as ParentNode | null;
  initTwStaticUsps(target ?? document);
});
